import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Button,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Tooltip,
} from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import {
    IconCancel,
    IconMetadata,
    IconOpenInNew,
    IconResetSmall,
    IconSchema,
} from "@mzc-cloudplex/icons";
import { StyledGrid } from "@components_v2/grid/grid.styled";
import PanelPropertyTitle from "@components/view/components/panel-property-title";
import MetadataFieldEditMode from "@features/metadata-fields/metadata-field-edit-mode";
import MetadataSkeletonCreateAsset from "@routes/metadata-skeleton-create-asset";
import { isLocal } from "@cores/enviromentExtension";
import useCustomContent from "@hooks/useCustomContent";
import useSettings from "@hooks/useSettings";
import tryParse from "@modules/settingParser";
import { setValidationError, updateData } from "@modules/createAsset";
import { getContentSchemaDetail } from "@modules/content-schema";
import { getContentSchemasAPI } from "@modules/apis/content-schemas";

const LX_PURCHASE_FIELD_ID = "jWyYPagVkBnZ3VOp2bw7qDGL";
const LX_UAS_FIELD_ID = "PbV4RZWel5ODxONoJ8jBG172";

const CreateAssetCustomMetadataSection = ({ pendingCreate, initialCustomMetadata }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { getCustomMetadataByContentSchema, checkIsNull } = useCustomContent();
    const { getSettingData } = useSettings({ type: "DomainStatus" });

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const isLxHausys = window.location.hostname.includes("lxhausys.");

    const contentSchemaMetadata = useSelector(
        ({ createAsset }) => createAsset.data.contentSchemaMetadata,
    );

    const [customMetadataSchema, setCustomMetadataSchema] = useState({
        data: [],
        pending: false,
        defaultCustomMetadataId: null,
    });

    const initContentSchemaMetadata = useCallback(
        (contentSchema) => {
            dispatch(updateData({ contentSchemaMetadata: contentSchema }));
        },
        [dispatch],
    );

    const loadDetailCustomMetadata = async (customMetadataId) => {
        try {
            const customMetadata = await dispatch(
                getContentSchemaDetail(customMetadataId, project.id),
            );
            const parsedCustomMetadata = getCustomMetadataByContentSchema(customMetadata);
            initContentSchemaMetadata(parsedCustomMetadata);
        } catch (error) {
            console.error("Failed to load default custom metadata:", error);
        }
    };

    const loadDefaultCustomMetadata = async (resourceSettings) => {
        try {
            let defaultCustomMetadataId = null;

            if (resourceSettings?.Asset?.defaultCustomMetadata?.value) {
                const defaultCustomMetadata = tryParse(
                    resourceSettings,
                    "Asset",
                    "defaultCustomMetadata",
                    2,
                    true,
                );
                defaultCustomMetadataId = defaultCustomMetadata?.value?.id;
                if (defaultCustomMetadataId) {
                    await loadDetailCustomMetadata(defaultCustomMetadataId);
                }
            }
            return defaultCustomMetadataId;
        } catch (e) {
            console.log(e);
        }
    };

    const handleInitialCustomMetadata = async () => {
        setCustomMetadataSchema((prev) => ({ ...prev, pending: true }));
        const resourceSettings = await getSettingData("Asset");
        const customMetadataParams = { type: "EXTENDED_METADATA", status: "ACTIVE" };
        const customMetadataData = await getContentSchemasAPI(
            stage.id,
            stage.endpoint,
            project.id,
            customMetadataParams,
        );
        let defaultCustomMetadataId = null;
        if (!initialCustomMetadata) {
            defaultCustomMetadataId = await loadDefaultCustomMetadata(resourceSettings);
        } else if (initialCustomMetadata?.customMetadataId) {
            loadDetailCustomMetadata(initialCustomMetadata.customMetadataId);
        }
        setCustomMetadataSchema((prev) => ({
            ...prev,
            data: customMetadataData.data,
            defaultCustomMetadataId,
            pending: false,
        }));
    };

    useEffect(() => {
        handleInitialCustomMetadata();
    }, []);

    const onHandleChangeCustomMetadata = useCallback(
        async (id, value, validationErrors) => {
            const metadata = [...(contentSchemaMetadata?.fields ?? [])];
            metadata?.forEach((meta) => {
                if (meta.id === id) {
                    meta.value = value;
                }
                if ((isLocal || isLxHausys) && meta.id === LX_PURCHASE_FIELD_ID) {
                    const isPurchaseSelected = value === "구매";
                    const uasField = metadata.find((field) => field.id === LX_UAS_FIELD_ID);
                    if (uasField) {
                        uasField.isRequired = isPurchaseSelected;
                    }
                }
            });
            dispatch(
                updateData({
                    contentSchemaMetadata: { ...contentSchemaMetadata, fields: metadata },
                }),
            );
            dispatch(setValidationError({ key: `custom-metadata-${id}`, error: validationErrors }));
        },
        [contentSchemaMetadata, dispatch, isLxHausys],
    );

    const assetCustomMetadata = useMemo(
        () =>
            contentSchemaMetadata?.fields?.map((meta) => ({
                ...meta,
                onChangeField: onHandleChangeCustomMetadata,
            })),
        [contentSchemaMetadata, onHandleChangeCustomMetadata],
    );

    const shouldShowUasLinkButton = useCallback(() => {
        if (!assetCustomMetadata) {
            return false;
        }
        if (!isLxHausys) {
            return false;
        }
        const purchaseField = assetCustomMetadata.find((field) => field.id === LX_PURCHASE_FIELD_ID);
        const uasField = assetCustomMetadata.find((field) => field.id === LX_UAS_FIELD_ID);
        if (purchaseField && uasField) {
            const isPurchaseSelected = purchaseField.value === "구매";
            if (isPurchaseSelected) {
                return true;
            }
        }
        return false;
    }, [assetCustomMetadata, isLxHausys]);

    return (
        <>
            <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                <Grid container flexDirection={"row"} alignItems={"center"}>
                    <Grid item flex={1}>
                        <Typography
                            variant={`body1`}
                            component={"p"}
                            color={theme.palette.secondary[800]}
                        >
                            {t(`common::label::Custom Metadata`)}
                        </Typography>
                    </Grid>
                    <Select
                        variant={"filled"}
                        displayEmpty
                        onChange={(e) => loadDetailCustomMetadata(e.target.value)}
                        sx={{
                            backgroundColor: theme.palette.common.white,
                        }}
                        value={contentSchemaMetadata?.id ?? null}
                        renderValue={() => {
                            const selected = contentSchemaMetadata?.id;
                            if (!selected) {
                                return (
                                    <Grid
                                        container
                                        flexDirection={"row"}
                                        gap={1}
                                        alignItems="center"
                                    >
                                        <IconSchema size={16} />
                                        <Typography
                                            variant={"body1"}
                                            color={theme.palette.secondary[800]}
                                        >
                                            {t(`common::label::Load custom metadata schema`)}
                                        </Typography>
                                    </Grid>
                                );
                            }

                            const selectedTemplate = customMetadataSchema?.data?.results?.find(
                                (template) => template.id === selected,
                            );

                            return (
                                <Grid container flexDirection={"row"} gap={1} alignItems="center">
                                    <IconMetadata size={16} color={theme.palette.secondary[700]} />
                                    <Typography variant="body1">{selectedTemplate?.name}</Typography>
                                </Grid>
                            );
                        }}
                    >
                        {customMetadataSchema?.pending ? (
                            <MenuItem>
                                <Grid container flexDirection={"column"} px={1.5} py={0.5} gap={1}>
                                    {new Array(3).fill("").map((_, i) => (
                                        <Grid item key={`custom-metadata-schema-skeleton-${i}`}>
                                            <Skeleton
                                                variant={"rounded"}
                                                animation={"wave"}
                                                width={150}
                                                height={22}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </MenuItem>
                        ) : (
                            customMetadataSchema?.data?.results?.map((option, index) => (
                                <MenuItem key={`metadataTemplate-${index}`} value={option.id}>
                                    <Typography component="span" variant="body1">
                                        {option.name}
                                    </Typography>
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </Grid>
                <StyledGrid item theme={theme}>
                    {customMetadataSchema.pending ? (
                        <MetadataSkeletonCreateAsset />
                    ) : (
                        <>
                            <Grid container flexDirection={"row"} alignItems={"center"}>
                                <Grid item flex={1}>
                                    {assetCustomMetadata?.length > 0 ? (
                                        <Stack
                                            direction={"row"}
                                            spacing={1}
                                            alignItems={"center"}
                                        >
                                            <IconSchema size={16} color={"success"} />
                                            <Typography
                                                variant={"body1_500"}
                                                color={theme.palette.greySecondary.main}
                                            >
                                                {contentSchemaMetadata?.name}
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <Typography color={theme.palette.greySecondary[400]}>
                                            {t(
                                                "common::label::Please select a custom metadata preset.",
                                            )}
                                        </Typography>
                                    )}
                                </Grid>
                                {shouldShowUasLinkButton() && (
                                    <Grid item sx={{ mr: theme.spacing(2.5) }}>
                                        <Button
                                            size={"xsmall"}
                                            variant={"contained"}
                                            color={"greySecondary"}
                                            endIcon={
                                                <IconOpenInNew
                                                    size={16}
                                                    htmlColor={theme.palette.common.white}
                                                />
                                            }
                                            onClick={() =>
                                                window.open(
                                                    "http://guas.lxhausys.com/cm/service/html.ncd?view=/lgerp/pd/retrievePDList&topId",
                                                    "_blank",
                                                )
                                            }
                                        >
                                            UAS 품의서 조회
                                        </Button>
                                    </Grid>
                                )}
                                <Grid item>
                                    <Tooltip title={t(`common::label::Delete`)}>
                                        <IconButton
                                            size="xsmall"
                                            circled
                                            disabled={
                                                !assetCustomMetadata ||
                                                assetCustomMetadata?.length === 0
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                initContentSchemaMetadata(null);
                                            }}
                                        >
                                            <IconCancel
                                                size={12}
                                                htmlColor={theme.palette.grey[500]}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                <Divider
                                    orientation={"vertical"}
                                    flexItem={true}
                                    sx={{ mr: 1, ml: 1.5 }}
                                />
                                <Grid item>
                                    <Button
                                        disabled={
                                            !assetCustomMetadata ||
                                            assetCustomMetadata?.length === 0
                                        }
                                        size={"small"}
                                        variant={"text"}
                                        startIcon={<IconResetSmall size={16} />}
                                        color={"greySecondary"}
                                        onClick={() => {
                                            initialCustomMetadata ||
                                            customMetadataSchema.defaultCustomMetadataId
                                                ? loadDetailCustomMetadata(
                                                      initialCustomMetadata?.customMetadataId ??
                                                          customMetadataSchema.defaultCustomMetadataId,
                                                  )
                                                : initContentSchemaMetadata(null);
                                        }}
                                    >
                                        <Typography variant={"body3"}>
                                            {t(`common::label::Reset`)}
                                        </Typography>
                                    </Button>
                                </Grid>
                            </Grid>
                            <Divider flexItem={true} sx={{ m: "20px 0" }} />
                            {assetCustomMetadata?.map((field) => (
                                <Grid
                                    key={`metadata-field-${field.id}-wrapper`}
                                    container
                                    alignItems={"center"}
                                    mb={2.5}
                                >
                                    <Grid
                                        item
                                        sx={{ alignSelf: "flex-start", minWidth: "244px" }}
                                        alignItems={"center"}
                                        mt={theme.spacing(1)}
                                    >
                                        <PanelPropertyTitle
                                            variant={"body1"}
                                            subtitle={field.subtitle}
                                            required={field.isRequired}
                                            tooltip={field.description}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        sx={{
                                            flex: 1,
                                            alignSelf: "flex-start",
                                        }}
                                    >
                                        <MetadataFieldEditMode
                                            id={field.id}
                                            type={field.type}
                                            value={field.value}
                                            options={field.options}
                                            disabled={pendingCreate || !field.isEditable}
                                            validationRule={field.validationRule}
                                            onChangeField={field.onChangeField}
                                        />
                                    </Grid>
                                </Grid>
                            ))}
                        </>
                    )}
                </StyledGrid>
            </Grid>
        </>
    );
};

export default CreateAssetCustomMetadataSection;
